import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.less';
import DistrictBounds from './DistrictBounds';
import DistrictColor from './DistrictColor';
import ClusterMarkerOfDistrict from './ClusterMarkerOfDistrict';
import { CITY_LEVEL } from '@/common/components/AMap/ts-config';
import { useDebounceEffect } from '@/common/hook/useEffectDebounce';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { message } from 'antd';
import { CollectListContext, CLUSTER_CRITICAL_COUNT } from '../../../ts-config';
import Point from './Point';
import { creating, collecting, allData, businessStatus } from '../ts-config';

/**
 * @Description 地图相关内容入口
 */
const MapCon:FC<{
  cityProvinceInfo, // 左上角城市信息
  mapIns,
  level,
  curSelectDistrict,
  districtData,
  itemData,
  setItemData,
  locationInfo,
  polygonData,
  rankSort,
  isShape,
  isSelectDistrictRef,
  isOpenBorder,
  isBatchCollect, // 是否是批量收藏状态收藏
  batchCollectIds, // 批量收藏的开店区域id
  setBatchCollectIds, // 设置批量收藏的开店区域id
  setPointDataIds, // 设置屏幕内展示的点id
}> = ({
  cityProvinceInfo, // 左上角城市信息
  mapIns,
  level,
  curSelectDistrict,
  districtData,
  itemData,
  setItemData,
  locationInfo,
  polygonData,
  rankSort,
  isShape,
  isSelectDistrictRef,
  isOpenBorder,
  isBatchCollect, // 是否是批量收藏状态收藏
  batchCollectIds, // 批量收藏的开店区域id
  setBatchCollectIds, // 设置批量收藏的开店区域id
  setPointDataIds, // 设置屏幕内展示的点id
}) => {
  const { collectList }: any = useContext(CollectListContext);

  const [pointData, setPointData] = useState<any[]>([]);
  const [curBounds, setCurBounds] = useState<any>();// 存放高德获取到的bounds，与entry的bounds(最大最小经纬度)不同
  const polygonListRef = useRef<any[]>([]);// 商圈围栏
  const [curZoom, setCurZoom] = useState<number>(10);

  // 过滤排名数据
  const districtDataRank = useMemo(() => {
    const { value } = rankSort;
    // if (value > 0) {
    return districtData.filter((item) => {
      if (value === creating) { // 生成中的数据
        return item?.status === businessStatus.NEW;
      } else if (value === collecting) { // 被收藏的数据
        return collectList.includes(item?.id);
      } else if (value === allData) { // 全部数据
        return true;
      } else {
        return item.rank < value;
      }
    });
    // }
    // return districtData;
  }, [rankSort, districtData, collectList]);

  // 是否展示点聚合
  const isShowClusterMarkerOfDistrict = useMemo(() => {
    return pointData?.length > 500;
  }, [pointData]);

  const polygonDataRank = useMemo(() => {
    const ids = districtDataRank.map((item) => item.id);
    return polygonData.filter((item) => ids.includes(item.id));
  }, [polygonData, districtDataRank]);

  useEffect(() => {
    if (!mapIns) return;
    mapIns.on('complete', () => moveEndHandle());
    mapIns.on('moveend', () => moveEndHandle());
    mapIns.on('zoomend', () => moveEndHandle());
  }, [mapIns]);

  useDebounceEffect(() => {
    if (!cityProvinceInfo?.id) return;
    message.config({ maxCount: 1 });
    if (cityProvinceInfo?.provinceName && cityProvinceInfo?.name && !isSelectDistrictRef.current) {
      V2Message.info(`当前已切换到${cityProvinceInfo?.provinceName}/${cityProvinceInfo?.name}`);
    } else {
      isSelectDistrictRef.current = false;
    }
    // message.config({ maxCount: 1 });
  }, [cityProvinceInfo?.id], 300);

  const moveEndHandle = () => {
    const zoom = mapIns.getZoom();
    setCurZoom(zoom);
    const bounds = mapIns?.getBounds(); // 获取地图可视区域的坐标
    /**
       *  当一个屏幕在zoom为BUSINESS_ZOOM的情况下，
       *  maxLat-minLat约为0.05、maxLng-minLng约为0.1
       *  四个角各取1/4增大范围，能让用户在小范围移动时，已经提前加载出点位，同时能够让点位中心不在可视范围，但商圈围栏在可视范围内的也展示出来
       * 并且能够避免一个商圈很大的情况下，缩放到很小的可视范围，此时商圈中心点不在可视范围但是边缘能在可视范围但是却消失的情况
       */
    const target = {
      minLat: bounds?.southWest.lat - 0.05 / 4,
      maxLat: bounds?.northEast.lat + 0.05 / 4,
      minLng: bounds?.southWest.lng - 0.1 / 4,
      maxLng: bounds?.northEast.lng + 0.1 / 4,
    };

    setCurBounds(target);
  };

  useEffect(() => {
    if (!isOpenBorder) {
      setPointData([]);
      setPointDataIds([]);
      return;
    }
    const res = districtDataRank.filter((item) => item?.lng && item?.lat &&
    item.lng >= curBounds?.minLng && item.lng <= curBounds?.maxLng &&
    item.lat >= curBounds?.minLat && item.lat <= curBounds?.maxLat
    // curBounds?.contains(new window.AMap.LngLat(item.lng, item.lat))
    );
    // 获取点位数据对应的id，供请求围栏接口使用，所以这里不需要过滤200名后的数据
    setPointDataIds(level <= CITY_LEVEL ? [] : res.map((item) => item.id));
    setPointData(level <= CITY_LEVEL ? [] : res);
  }, [curBounds, curZoom, districtDataRank, level, isOpenBorder]);

  // 青山要求，在小围栏大于500个的情况下，不显示点位
  useEffect(() => {
    if (!mapIns) return;
    if (polygonDataRank?.length > CLUSTER_CRITICAL_COUNT) {
      mapIns.setFeatures(['bg', 'road', 'building']);
    } else {
      mapIns.setFeatures(['bg', 'road', 'building', 'point']);
    }
  }, [polygonDataRank, mapIns]);
  const handlePoint = () => {
    return pointData;
  };
  // const handlePointWithZoom = () => {
  //   let _pointData = pointData;
  //   if (curZoom >= BUSINESS_ZOOM) {
  //     _pointData = _pointData.filter((item) => item.rank < 200);
  //   }
  //   return _pointData;
  // };
  return <div className={styles.mapCon}>

    {/* 数据量比较大时的区级别聚合 */}
    <ClusterMarkerOfDistrict
      level={level}
      mapIns={mapIns}
      data={pointData}
    />
    {/* 非聚合状态下显示的商圈 */}
    <Point
      mapIns={mapIns}
      pointData={pointData}
      level={level}
      itemData={itemData}
      setItemData={setItemData}
      locationInfo={locationInfo}
      polygonData={polygonDataRank}
      allPointData={districtDataRank}
      isShape={isShape}
      curZoom={curZoom}
      polygonListRef={polygonListRef}
      isShowClusterMarkerOfDistrict={isShowClusterMarkerOfDistrict}
      isBatchCollect={isBatchCollect}
      batchCollectIds={batchCollectIds}
      setBatchCollectIds={setBatchCollectIds}
      viewAreaData={handlePoint()}
      cityProvinceInfo={cityProvinceInfo}
    />

    {/* 行政区围栏-左侧筛选选中时，会绘制该行政区围栏 */}
    <DistrictBounds
      mapIns={mapIns}
      level={level}
      curSelectDistrict={curSelectDistrict}
    />
    {/* 行政区色块围栏-市级状态下，会绘制该市的所有行政区围栏（不同颜色） */}
    <DistrictColor
      mapIns={mapIns}
      level={level}
      cityProvinceInfo={cityProvinceInfo}
    />
  </div>;
};
export default MapCon;
